import type { ReactNode } from "react";
import type { Medication } from "@/shared/types";
import { ISSUE_LABELS, SourceBadge } from "./medicationReviewBadges";

const statusLabels: Record<Medication["status"], string> = {
  active: "Active", discontinued: "Discontinued", on_hold: "On Hold", completed: "Completed",
};

function formatDate(dateString: string | undefined): string {
  // "" = the source never gave a usable date (see adaptMedication's toDateOnly).
  if (!dateString) return "Not available";
  const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  const date = new Date(dateString + "T00:00:00");
  if (Number.isNaN(date.getTime())) return "Not available";
  return `${months[date.getMonth()]} ${date.getDate()}, ${date.getFullYear()}`;
}

/** One card per reviewItemId. Records arrive already grouped (see groupReviewItems). */
export interface ReviewItemGroup {
  reviewItemId: string;
  records: Medication[];
}

/** Groups under-review medications by reviewItemId, preserving first-seen order. */
export function groupReviewItems(medications: Medication[]): ReviewItemGroup[] {
  const groups = new Map<string, Medication[]>();
  for (const med of medications) {
    if (!med.review) continue;
    const list = groups.get(med.review.reviewItemId) ?? [];
    list.push(med);
    groups.set(med.review.reviewItemId, list);
  }
  return [...groups].map(([reviewItemId, records]) => ({ reviewItemId, records }));
}

const COMPARED_ROWS = ["Medicine", "Dose", "Frequency", "Status"] as const;

function recordRows(med: Medication) {
  return [
    { label: "Medicine", value: med.name },
    { label: "Dose", value: med.dosage },
    { label: "Frequency", value: med.frequency },
    { label: "Status", value: statusLabels[med.status] },
    { label: "Date", value: formatDate(med.prescribedDate) },
  ];
}

const NOT_GIVEN = new Set(["", "not specified"]);
const comparable = (value: string) => value.trim().toLowerCase().replace(/\s+/g, " ");

/** Row labels whose values differ between the two sides of a pair — a value
 *  missing on one side ("Not specified") isn't a disagreement. */
export function differingRows(a: Medication, b: Medication): Set<string> {
  const rowsA = new Map(recordRows(a).map((r) => [r.label, comparable(r.value)]));
  const rowsB = new Map(recordRows(b).map((r) => [r.label, comparable(r.value)]));
  return new Set(
    COMPARED_ROWS.filter((label) => {
      const va = rowsA.get(label) ?? "";
      const vb = rowsB.get(label) ?? "";
      return !NOT_GIVEN.has(va) && !NOT_GIVEN.has(vb) && va !== vb;
    }),
  );
}

/** "Metformin HCl 500 mg tablet" -> "Metformin HCl": the name before its strength. */
function baseName(name: string): string {
  return name.split(/\s\d/)[0].trim() || name;
}

/**
 * Plain-language heading for a card: for a pair, what actually disagrees
 * ("Your records show different doses of Metformin"); otherwise the issue label.
 */
export function reviewTitle(records: Medication[]): string {
  const trigger = records[0].review!.triggerType;
  const existing = records.find((m) => m.review?.recordRole === "EXISTING");
  const incoming = records.find((m) => m.review?.recordRole === "INCOMING");
  if (existing && incoming) {
    const diff = differingRows(existing, incoming);
    const name = baseName(incoming.name);
    if (diff.has("Medicine") || diff.has("Dose")) return `Your records show different doses of ${name}`;
    if (diff.has("Frequency")) return `Your records show different schedules for ${name}`;
    if (diff.has("Status")) return `Your records disagree on whether you still take ${name}`;
  }
  return ISSUE_LABELS[trigger];
}

function RecordColumn({ title, med, extra, differs }: {
  title?: string; med: Medication; extra?: ReactNode; differs?: Set<string>;
}) {
  const rows = recordRows(med);
  return (
    <div className="rounded-2xl border border-[#E5E7EB] bg-[#F9FAFB] p-4" data-testid="review-record">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        {title && <p className="text-xs font-extrabold text-[#0A2947]">{title}</p>}
        <SourceBadge vendor={med.sourceVendor} />
      </div>
      <dl className="!m-0 grid gap-2">
        {rows.map(({ label, value }) => {
          const isDifferent = differs?.has(label) ?? false;
          return (
            <div
              key={label}
              data-differs={isDifferent || undefined}
              className={
                isDifferent
                  ? "-mx-2 flex items-baseline justify-between gap-3 rounded-lg bg-[#FEF6DC] px-2 py-1"
                  : "flex items-baseline justify-between gap-3"
              }
            >
              <dt className={isDifferent ? "text-xs font-semibold text-[#946200]" : "text-xs text-[#9CA3AF]"}>{label}</dt>
              <dd className={isDifferent ? "!m-0 text-right text-sm font-extrabold text-[#0A2947]" : "!m-0 text-right text-sm font-semibold text-[#0A2947]"}>
                {value}
              </dd>
            </div>
          );
        })}
      </dl>
      {extra}
    </div>
  );
}

/** True when a review item's records are an EXISTING + INCOMING pair. */
export function isPairItem(records: Medication[]): boolean {
  return records.some((m) => m.review?.recordRole === "EXISTING") && records.some((m) => m.review?.recordRole === "INCOMING");
}

/**
 * The records of one review item: a pair side by side ("Earlier record" /
 * "Newer record"), otherwise each record on its own. Shared by the conflict card
 * and the review screen so both show records identically.
 */
export function ReviewRecords({ records, renderExtra }: {
  records: Medication[];
  /** Optional content under each record's fields (the clinician screen's RxNorm block). */
  renderExtra?: (med: Medication) => ReactNode;
}) {
  const existing = records.find((m) => m.review?.recordRole === "EXISTING");
  const incoming = records.find((m) => m.review?.recordRole === "INCOMING");
  if (existing && incoming) {
    // What disagrees is highlighted on both sides, so it's the first thing seen.
    const differs = differingRows(existing, incoming);
    return (
      <div className="grid gap-3 sm:grid-cols-2">
        <RecordColumn title="Earlier record" med={existing} extra={renderExtra?.(existing)} differs={differs} />
        <RecordColumn title="Newer record" med={incoming} extra={renderExtra?.(incoming)} differs={differs} />
      </div>
    );
  }
  return (
    <div className="grid gap-3">
      {records.map((med) => <RecordColumn key={med.id} med={med} extra={renderExtra?.(med)} />)}
    </div>
  );
}

/**
 * The patient can respond only while the item waits on them. A record from a
 * backend that predates Module 3 carries no itemStatus; it's treated as
 * AWAITING_PATIENT, and the review screen re-checks the real status on load.
 */
export function awaitsPatientInput(itemStatus: string | undefined): boolean {
  return !itemStatus || itemStatus === "AWAITING_PATIENT" || itemStatus === "NEEDS_MORE_INFO";
}

/** Whether the Review button shows: the backend's canRespond when it sends
 *  one (it also allows one late answer once an item is with a clinician),
 *  otherwise the status rule above. */
export function canPatientRespond(review: Medication["review"]): boolean {
  if (!review) return false;
  return review.canRespond ?? awaitsPatientInput(review.itemStatus);
}

export const WAITING_FOR_CLINICIAN_LABEL = "Waiting for clinician review";
